import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  Checkbox,
  styled,
  Box,
  Skeleton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { PageConfig, SortConfig, ReferralData, DeduplicationMatch } from './types';
import TableRow from './Table/TableRow';
import { ColumnConfig } from './Table/cellRenderers';
import {
  LeadManagementFields,
  DisplayFieldItem,
  PatientProfileTabFields,
} from '../LeadManagement/LeadManagement.types';

export interface ExpandedFieldConfig {
  personal?: Array<{ key: string; label: string }>;
  demographics?: Array<{ key: string; label: string }>;
  caregiverInfo?: Array<{ key: string; label: string }>;
  trackingParams?: Array<{ key: string; label: string }>;
  dedupAddParams?: Array<{ key: string; label: string }>;
  auditChanges?: Array<{ key: string; label: string }>;
  ExpandedDisplayFields?: DisplayFieldItem[];
  // Header labels
  personalInfoHeaderText?: string;
  demographicsHeaderText?: string;
  caregiverInfoHeaderText?: string;
  trackingParamsHeaderText?: string;
  dedupAddParamsHeaderText?: string;
  commentsHeaderText?: string;
  viewDetails?: { href: string; text: string };
  sendToCC?: string;
  resetFilterText?: string;
}

interface CustomTableProps {
  config: PageConfig;
  someSelected: boolean;
  allSelected: boolean;
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  paginatedData: ReferralData[];
  handleSelectRow: (id: string) => void;
  onPatientCCIdChange?: (id: string, newValue: string) => void;
  onStatusChange?: (id: string, newValue: string) => void;
  sortConfigs?: SortConfig[];
  onSort?: (columnId: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  defaultSortField?: string;
  onLockToggle?: (id: string) => void;
  lockLoadingLeadId?: string | null;
  currentUser?: string;
  lockedRecordsCount?: number;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  hideLockColumn?: boolean;
  hideDedupeSelectColumn?: boolean;
  hideSendToCCButton?: boolean;
  isProcessedTab?: boolean;
  expandedConfig?: ExpandedFieldConfig;
  loading?: boolean;
  onRemoveRecord?: (patientId: string) => void;
  confirmationModalMessages?: LeadManagementFields;
  /** When set, expand this row (e.g. after successful lock). Cleared by parent via onExpandedRowIdApplied. */
  expandRowId?: string | null;
  onExpandedRowIdApplied?: () => void;
  /** Floating red alert for Send to CC lock validation (same UX as lock/unlock errors). */
  onSendToCCLockValidationError?: (message: string) => void;
  isLockingEnabled?: boolean;
  currentTabKey?: string;
  PatientProfileConfig?: PatientProfileTabFields;
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  border: `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
  overflow: 'visible',
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.neutral1?.main || '#F6F7FB',
    fontWeight: 600,
    fontSize: '13px',
    color: theme.palette.neutral8?.main || theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.neutral3?.main || theme.palette.divider}`,
    padding: theme.spacing(1.25, 1.5),
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  '& .MuiTableCell-body': {
    fontSize: '13px',
    color: theme.palette.neutral8?.main || theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.neutral3?.main || theme.palette.divider}`,
    padding: theme.spacing(1.25, 1.5),
  },
}));

const CustomTable = ({
  config,
  someSelected,
  allSelected,
  handleSelectAll,
  paginatedData,
  handleSelectRow,
  onPatientCCIdChange,
  onStatusChange,
  sortConfigs = [],
  onSort,
  sortBy,
  sortOrder,
  defaultSortField,
  onLockToggle,
  lockLoadingLeadId = null,
  currentUser = '',
  lockedRecordsCount = 0,
  onOpenMergeModal,
  hideLockColumn = false,
  hideDedupeSelectColumn = false,
  hideSendToCCButton = false,
  isProcessedTab = false,
  expandedConfig,
  loading = false,
  onRemoveRecord,
  confirmationModalMessages,
  expandRowId = null,
  onExpandedRowIdApplied,
  onSendToCCLockValidationError,
  isLockingEnabled = false,
  currentTabKey,
  PatientProfileConfig,
}: CustomTableProps) => {
  // State to track which row is currently expanded (accordion behavior)
  const [expandedRowId, setExpandedRowId] = React.useState<string | null>(null);

  // When parent requests expand (e.g. after successful lock), expand that row
  React.useEffect(() => {
    if (expandRowId) {
      setExpandedRowId(expandRowId);
      onExpandedRowIdApplied?.();
    }
  }, [expandRowId, onExpandedRowIdApplied]);

  // Prevent carrying expanded state across tabs.
  React.useEffect(() => {
    setExpandedRowId(null);
  }, [currentTabKey]);

  // Handler to toggle row expansion (only one row can be expanded at a time)
  const handleRowToggle = (rowId: string) => {
    setExpandedRowId((prevId) => (prevId === rowId ? null : rowId));
  };

  // Filter columns based on props
  const filteredColumns = config.columns.filter((column) => {
    if (hideLockColumn && column.id === 'lock') return false;
    return true;
  });

  const getSortOrder = (columnId: string) => {
    // Use new sortBy/sortOrder props if available, otherwise fall back to sortConfigs
    if (sortBy !== undefined) {
      if (sortBy === columnId) {
        return sortOrder || 'none';
      }
      return 'none';
    }
    // Legacy sortConfigs support
    const sortConfig = sortConfigs.find((s) => s.column === columnId);
    return sortConfig?.order || 'none';
  };

  const getSortIcon = (columnId: string) => {
    const order = getSortOrder(columnId);
    if (order === 'asc') {
      return <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.6 }} />;
    } else if (order === 'desc') {
      return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.6 }} />;
    }
    if (!sortBy && defaultSortField && columnId === defaultSortField) {
      return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.6 }} />;
    }
    return <SwapVertIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.6 }} />;
  };

  const handleSort = (columnId: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(columnId);
    }
  };

  return (
    <StyledTableContainer>
      <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <MuiTableRow>
            {filteredColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                padding={column.id === 'select' ? 'checkbox' : 'normal'}
                sx={{
                  width: column.width,
                  maxWidth: column.maxWidth || 'none',
                  cursor: column.sortable ? 'pointer' : 'default',
                  userSelect: 'none',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  '&:hover': column.sortable
                    ? {
                        backgroundColor: (theme: any) => theme.palette.neutral2?.main || '#F2F2F2',
                      }
                    : {},
                }}
                onClick={() => handleSort(column.id, column.sortable)}
              >
                {column.id === 'select' && config.enableSelection ? (
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: (theme: any) => theme.palette.neutral5?.main,
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: (theme: any) => theme.palette.themeColor?.main,
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent:
                        column.align === 'center'
                          ? 'center'
                          : column.align === 'right'
                            ? 'flex-end'
                            : 'flex-start',
                    }}
                  >
                    {column.label}
                    {column.sortable && getSortIcon(column.id)}
                  </Box>
                )}
              </TableCell>
            ))}
          </MuiTableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            // Show skeleton rows while loading
            Array.from({ length: 5 }).map((_, index) => (
              <MuiTableRow key={`skeleton-${index}`}>
                {filteredColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    sx={{
                      width: column.width,
                      maxWidth: column.maxWidth || 'none',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    <Skeleton variant="text" width="100%" height={20} />
                  </TableCell>
                ))}
              </MuiTableRow>
            ))
          ) : paginatedData.length === 0 ? (
            <MuiTableRow>
              <TableCell colSpan={filteredColumns.length} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                  {config.noDataMessage || 'No data available'}
                </Typography>
              </TableCell>
            </MuiTableRow>
          ) : (
            paginatedData.map((row: any) => (
              <TableRow
                key={row.patientId}
                row={row}
                columns={filteredColumns as ColumnConfig[]}
                onSelectRow={handleSelectRow}
                onPatientCCIdChange={onPatientCCIdChange}
                onStatusChange={onStatusChange}
                currentUser={currentUser}
                lockedRecordsCount={lockedRecordsCount}
                onLockToggle={onLockToggle}
                lockLoadingLeadId={lockLoadingLeadId}
                enableExpandableRows={config?.enableExpandableRows || false}
                onOpenMergeModal={onOpenMergeModal}
                hideDedupeSelectColumn={hideDedupeSelectColumn}
                hideSendToCCButton={hideSendToCCButton}
                isProcessedTab={isProcessedTab}
                expandedConfig={expandedConfig}
                isExpanded={expandedRowId === row.patientId}
                onToggleExpand={() => handleRowToggle(row.patientId)}
                onRemoveRecord={onRemoveRecord}
                confirmationModalMessages={confirmationModalMessages}
                onSendToCCLockValidationError={onSendToCCLockValidationError}
                isLockingEnabled={isLockingEnabled}
                currentTabKey={currentTabKey}
                filterConfig={config?.filterConfig}
                PatientProfileConfig={PatientProfileConfig}
              />
            ))
          )}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default CustomTable;
